<template>
  <section>
    <base-select
      :select-options="options"
      :model-value="estimatedTimeOfArrival"
      :required="estimatedTimeOfArrivalCompulsory"
      :validation-rules="validationRules"
      name="estimated_time_of_arrival"
      @change="changeHandler"
    >
      <template #label>
        <translate>Estimated time of arrival</translate>
      </template>
      <template #helpText>
        <translate tag="span" :tt="{ campsiteName }">
          Requested by {campsiteName}
        </translate>
      </template>
      <template #errorMessage>
        <translate>Please tell us estimated time of arrival</translate>
      </template>
    </base-select>

    <translate tag="p" class="additional-info">
      Please use the special requests box above if you wish to provide more
      information
    </translate>
  </section>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-unsafe-member-access -- TODO: Type event properly */
import * as z from 'zod'
const { i18n } = useI18n()

interface ArrivalTime {
  hour: number
  minute: number
}

const props = defineProps<{
  campsiteName: string
  estimatedTimeOfArrival?: string | null
  estimatedTimeOfArrivalCompulsory?: boolean
  arrivalFrom?: ArrivalTime
  arrivalTo?: ArrivalTime
}>()

// If no value provided, set to 00:00
const arrivalFrom = computed(() => {
  return props.arrivalFrom || { hour: 0, minute: 0 }
})

// If no value provided, set to 00:00, check below will set to 00:00 of
// following day
const arrivalTo = computed(() => {
  return props.arrivalTo || { hour: 0, minute: 0 }
})

const emit = defineEmits(['update:estimatedTimeOfArrival'])

const options = computed(() => {
  const options: {
    name: string
    value: string
    selected?: boolean
  }[] = []

  if (!props.estimatedTimeOfArrivalCompulsory) {
    options.push({
      name: '---',
      value: '',
    })
  }

  const arrivalFromDate = new Date(
    Date.UTC(1970, 0, 1, arrivalFrom.value.hour, arrivalFrom.value.minute),
  )

  const arrivalToDate = new Date(
    Date.UTC(1970, 0, 1, arrivalTo.value.hour, arrivalTo.value.minute),
  )

  // Bit of a hack but since we have no dates we assume any latest arrival time
  // before 5am is after midnight on arrival day. e.g. Arrive:	Anytime - 01:00 a.m.
  if (arrivalTo.value.hour < 5) {
    arrivalToDate.setUTCDate(arrivalToDate.getUTCDate() + 1)
  }

  const optionStartDate = new Date(arrivalFromDate)
  const optionEndDate = new Date(optionStartDate)
  optionEndDate.setUTCMinutes(0)
  optionEndDate.setUTCHours(optionEndDate.getUTCHours() + 1)

  while (optionStartDate < arrivalToDate) {
    const formattedFrom = new Intl.DateTimeFormat(i18n.locale, {
      hour: 'numeric',
      minute: 'numeric',
      timeZone: 'UTC',
    }).format(optionStartDate)

    const formattedTo = new Intl.DateTimeFormat(i18n.locale, {
      hour: 'numeric',
      minute: 'numeric',
      timeZone: 'UTC',
    }).format(optionEndDate)

    options.push({
      name: `${formattedFrom} - ${formattedTo}`,
      value: `${formattedFrom} - ${formattedTo}`,
      selected:
        props.estimatedTimeOfArrival === `${formattedFrom} - ${formattedTo}`,
    })

    optionStartDate.setUTCHours(optionStartDate.getUTCHours() + 1)
    optionStartDate.setUTCMinutes(0)

    if (
      optionStartDate.getUTCDate() === arrivalToDate.getUTCDate() &&
      optionStartDate.getUTCHours() + 1 > arrivalTo.value.hour
    ) {
      optionEndDate.setUTCMinutes(arrivalTo.value.minute)
    } else {
      optionEndDate.setUTCHours(optionStartDate.getUTCHours() + 1)
    }
  }

  return options
})

function changeHandler(event) {
  emit('update:estimatedTimeOfArrival', event.selectedValue)
}

const validationRules = computed(() => {
  if (props.estimatedTimeOfArrivalCompulsory) {
    return z
      .string()
      .transform((value) => value.trim())
      .pipe(
        z
          .string({ required_error: 'required' })
          .min(1, { message: 'required' }),
      )
  } else {
    return z.string().optional()
  }
})
</script>

<style scoped>
section :deep(.select) {
  width: 100%;
  font-size: 1.24em;
  margin: 0;
}

section :deep(.additional-info) {
  margin-top: 1em;
  color: var(--colour-text-light);
  font-size: var(--body);
}
</style>
